import { Component, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Store } from '@ngrx/store';
import {
  divide as _divide,
  escape as _escape,
  get as _get,
  round as _round,
  uniq as _uniq
} from 'lodash';
import { combineLatest, Observable, Subject } from 'rxjs';
import { filter, map, take, takeUntil, tap, withLatestFrom } from 'rxjs/operators';

// Misc
import { env } from '@amfam/environment';
import { PolicySelectors } from '@amfam/policy/data-access';
import {
  ANSRequestModel,
  Coverage,
  CoverageChangeCustomerTemplateDataModel,
  CoverageChangeModel,
  CoverageChangeNotificationRequestCustomerEmail,
  CustomerEmailRequest,
  Policy,
  QuoteModel,
  ReplaceVehicleRequestCustomerEmail,
  RiskModel,
  SubmitOnlineBindRequestModel
} from '@amfam/policy/models';
import { userQuery } from '@amfam/shared/user';
import { Agent, agentQuery, fromAgentActions, initialAgent } from '@amfam/shared/utility/agent';
import { BrandSelectors } from '@amfam/shared/utility/brand';
import { ImpersonateRolesService } from '@amfam/shared/utility/impersonation';
import { fromRouterActions } from '@amfam/shared/utility/navigation';
import { CopyService, UtilService } from '@amfam/shared/utility/shared-services';
import {
  DockingBarService,
  ErrorObj,
  LoadingSpinnerService,
  PageTitleService
} from '@amfam/ui-kit';

import * as fromRoot from '../../../core/store';
import { ReplaceVehicleService } from '../../../core/store/replace-vehicle/replace-vehicle.service';
import * as coverageChangeSelector from '../../+state';
import {
  CoverageChangeOnlineBindRequestAction,
  CoverageChangeRequestANSNotificationAction,
  CoverageChangeRequestCustomerEmailNotificationAction,
  CoverageChangeRequestNotificationAction,
  CoverageChangeSetRequestType,
  SendCoverageAnalyticsAction
} from '../../+state/coverage-change.actions';

@Component({
  selector: 'ds-coverage-change-review-quote',
  templateUrl: './coverage-change-review-quote.component.html',
  styleUrls: ['./coverage-change-review-quote.component.scss'],
  standalone: false
})
export class CoverageChangeReviewQuoteComponent implements OnInit {
  private vehicleId: string;
  newQuote$: Observable<QuoteModel> = this.store.select(
    coverageChangeSelector.getQuoteDetailsForNewCoverage
  );
  currentQuote$: Observable<QuoteModel>;
  currentVehicle$: Observable<RiskModel>;
  collisionCoverageValue$: Observable<string> = this.store.select(
    coverageChangeSelector.getSelectedCoverageLimitsByCode,
    'PACollisionCov'
  );
  rentalReimbursementValue$: Observable<string> = this.store.select(
    coverageChangeSelector.getSelectedCoverageLimitsByCode,
    'PARentalCov'
  );
  comprehensiveCoverageValue$: Observable<string> = this.store.select(
    coverageChangeSelector.getSelectedCoverageLimitsByCode,
    'PAComprehensiveCov'
  );

  ersValue$: Observable<string | boolean> = this.store.select(
    coverageChangeSelector.getSelectedERSCoverage,
    'PAEmergencyRoadServiceCov_af'
  );

  premiumDetails: any = {
    quoteExpirationDate: new Date(),
    current: {},
    new: {}
  };

  private partnerId: string;
  private customerId: string;
  private customerEmail: string;
  private customerName: string;
  private customerFirstName: string;
  private customerLastName: string;
  private customerPhone: string;

  private selectedPolicy: Policy;
  private selectedProducer: Agent;

  public newVehicle: string;
  public currentVehicle = 'current vehicle';
  private matchingvehicle: RiskModel;
  private stateCode: string;

  private drivers: string;
  private coverages = '';
  private newCoverages: string;
  private discounts: string;
  private fees: string;
  private rentalCoverageValue: string;
  private ersValue: boolean;

  errorMessage: string;
  public errorList: Array<ErrorObj> = [];

  // Shared subject for completing observables
  private stop$: Subject<void> = new Subject<void>();

  constructor(
    private store: Store<fromRoot.RootState>,
    public route: ActivatedRoute,
    private pageTitle: PageTitleService,
    private dockingBarService: DockingBarService,
    public roleService: ImpersonateRolesService,
    private replaceVehicleService: ReplaceVehicleService,
    private copyService: CopyService,
    private spinner: LoadingSpinnerService,
    private utilService: UtilService
  ) {}

  ngOnInit() {
    this.pageTitle.set('Make Policy Changes');
    this.dockingBarService.registerHeading('Make Policy Changes');
    this.spinner.stop();

    combineLatest(
      this.route.params,
      this.store.select(PolicySelectors.selectAdvanceAutoPolicies),
      this.store.select(PolicySelectors.selectAdvanceAutoPolicyRisks),
      this.store.select(userQuery.selectUserStateCode),
      this.store.select(agentQuery.getAgentEntities),
      this.store.select(coverageChangeSelector.getQuoteDetailsForNewCoverage),
      this.store.select(coverageChangeSelector.getSelectedCoverageLimits),
      (
        params,
        policies,
        risks,
        userState,
        agents,
        newQuoteDetails,
        selectedCoverageLimits: CoverageChangeModel
      ) => {
        return {
          vehicleId: params['id'],
          policies: policies,
          vehicles: risks,
          userState: userState,
          agents: agents,
          newQuoteDetails,
          selectedCoverageLimits: selectedCoverageLimits
        };
      }
    )
      .pipe(take(1))
      .subscribe(state => {
        this.vehicleId = state.vehicleId;
        this.stateCode = state.userState;

        const matchingVehicle = state.vehicles.find(vehicle => {
          return _get(vehicle, 'id') === this.vehicleId;
        });

        if (matchingVehicle) {
          const policyNumber = matchingVehicle.policyNumber;
          this.matchingvehicle = matchingVehicle;

          const matchingPolicy = state.policies.find(policy => {
            return _get(policy, 'policyNumber') === policyNumber;
          });

          if (matchingPolicy) {
            if (
              this.matchingvehicle &&
              this.matchingvehicle.year &&
              this.matchingvehicle.make &&
              this.matchingvehicle.model
            ) {
              this.currentVehicle =
                this.matchingvehicle.year +
                ' ' +
                this.matchingvehicle.make +
                ' ' +
                this.matchingvehicle.model;
            }
            this.selectedPolicy = matchingPolicy;
            // JP: Check if we've loaded producer already - dispatch load if not
            this.selectedProducer =
              state.agents[_get(this.selectedPolicy, 'assignedProducer.producerIdNum')];
            if (!this.selectedProducer) {
              this.store.dispatch(
                new fromAgentActions.LoadAgentAction(
                  Object.assign({}, initialAgent, {
                    id: _get(this.selectedPolicy, 'assignedProducer.producerIdNum')
                  })
                )
              );
            }

            this.premiumDetails = Object.assign(
              {},
              { new: state.newQuoteDetails, orderNumber: state.newQuoteDetails.workOrderNumber }
            );
          }
        }

        const comrehensiveCoverage = state.selectedCoverageLimits.riskCoverages.find(
          riskCoverage => riskCoverage.code === 'PAComprehensiveCov'
        );
        const comprehensiveValue = _get(comrehensiveCoverage, 'terms[0].selectedValue', '');
        const collisionCoverage = state.selectedCoverageLimits.riskCoverages.find(
          riskCoverage => riskCoverage.code === 'PACollisionCov'
        );
        const collisionValue = _get(collisionCoverage, 'terms[0].selectedValue', '');

        const rentalCoverage = state.selectedCoverageLimits.riskCoverages.find(
          riskCoverage => riskCoverage.code === 'PARentalCov'
        );
        this.ersValue = !!state.selectedCoverageLimits.riskCoverages.find(
          riskCoverage => riskCoverage.code === 'PAEmergencyRoadServiceCov_af'
        );

        this.rentalCoverageValue = rentalCoverage ? rentalCoverage.terms[0].selectedValue : '';
        this.newCoverages = comprehensiveValue ? `Comprehensive: $${comprehensiveValue}` : '';

        this.newCoverages += collisionValue ? `<br></br>Collision: $${collisionValue}` : '';

        const selectionArray: string[] = [];
        const [rraDailyAmount, maximumLimit] = this.rentalCoverageValue
          ? this.rentalCoverageValue.split('/')
          : ['', ''];

        if (this.rentalCoverageValue) {
          this.newCoverages += `<br></br>Rental Reimbursement: $${rraDailyAmount}/Daily Limit, $${maximumLimit}/Maximum`;
          selectionArray.push('Rental Reimbursement');
        }

        if (this.ersValue) {
          this.newCoverages += `<br></br>Emergency Roadside Assistance`;
          selectionArray.push('Emergency Roadside Assistance');
        }

        this.store.dispatch(
          new SendCoverageAnalyticsAction({
            pageAnalyticName: 'Review',
            eventName: 'Coverage Change',
            selection: selectionArray,
            rraAmount: rraDailyAmount
          })
        );
      });

    // JP: After Load action has been dispatched, subscribe again and fetch the correct producer
    if (!this.selectedProducer) {
      combineLatest(
        this.store.select(agentQuery.getAreAllAgentsLoaded),
        this.store.select(agentQuery.getAgentEntities),
        (loaded, agents) => {
          return {
            loaded: loaded,
            agents: agents
          };
        }
      )
        .pipe(
          filter(data => data.loaded),
          takeUntil(this.stop$)
        )
        .subscribe(data => {
          this.selectedProducer =
            data.agents[_get(this.selectedPolicy, 'assignedProducer.producerIdNum')];
        });
    }

    combineLatest(
      this.store.select(userQuery.selectCustomerId),
      this.store.select(userQuery.selectEmailAddress),
      this.store.select(userQuery.selectPrimaryPhone),
      this.store.select(userQuery.selectFirstName),
      this.store.select(userQuery.selectLastName),
      this.store.select(BrandSelectors.selectPartnerId),
      this.store.select(BrandSelectors.selectIsPartner),
      (
        customerId,
        userEmailAddress,
        userPrimaryPhone,
        userFirstName,
        userLastName,
        partner,
        isPartner
      ) => ({
        customerId,
        userEmailAddress,
        userPrimaryPhone,
        userFirstName,
        userLastName,
        partner,
        isPartner
      })
    ).subscribe(res => {
      this.partnerId = res.partner;
      this.customerFirstName = _get(res, 'userFirstName', '');
      this.customerLastName = _get(res, 'userLastName', '');
      this.customerName = this.customerFirstName + ' ' + this.customerLastName;
      this.customerId = _get(res, 'user.customerId', '');
      this.customerEmail = _get(res, 'userEmailAddress', '');
      this.customerPhone = _get(
        res,
        'userPrimaryPhone.tenDigitPhoneNumber',
        'No phone number on record'
      );
    });

    this.currentVehicle$ = this.route.params.pipe(
      map(params => params['id']),
      take(1),
      withLatestFrom(this.store.select(fromRoot.getReplaceVehicleRisks)),
      map(([vehicleId, vehicles]) => vehicles.find(risk => risk.id === vehicleId)),
      tap(currentVehicle => {
        this.pageTitle.set('Make Changes to ' + _get(currentVehicle, 'description', ''));
        this.dockingBarService.registerHeading(
          'Make Changes to ' + _get(currentVehicle, 'description', '')
        );
      })
    );

    /**
     * AS : Setup the existing quote model, along with other variables
     */
    this.currentQuote$ = this.currentVehicle$.pipe(
      take(1),
      withLatestFrom(this.store.select(PolicySelectors.selectAdvanceAutoPolicies)),
      map(([currentVehicle, policies]) =>
        policies.find(policy => {
          return _get(policy, 'policyNumber') === _get(currentVehicle, 'policyNumber');
        })
      ),
      map((currentPolicy): QuoteModel => {
        const currentTermMonths = Number(currentPolicy.termDescription.split(' ')[0]);
        return {
          description: 'Current',
          fullCost: Number(_get(currentPolicy, 'totalPremium', 0)).toFixed(2),
          monthlyCost: _round(_divide(currentPolicy.totalPremium, currentTermMonths), 2).toFixed(2),
          policyTermMonths: currentTermMonths + ' months'
        };
      })
    );
  }

  private getMessageModel(withoutBreak = false): CoverageChangeCustomerTemplateDataModel {
    {
      for (const vehicleCoverage of <Coverage[]>(
        _uniq(<Coverage[]>_get(this.matchingvehicle, 'vehicleCoverages', [])).filter(
          coverage =>
            coverage.code === 'PACollisionCov' ||
            coverage.code === 'PAComprehensiveCov' ||
            coverage.code === 'PARentalCov' ||
            coverage.code === 'PAEmergencyRoadServiceCov_af'
        )
      )) {
        if (this.coverages) {
          this.coverages += '<br></br>';
        }
        if (
          (_get(vehicleCoverage, 'code') === 'PACollisionCov' ||
            _get(vehicleCoverage, 'code') === 'PAComprehensiveCov') &&
          _get(vehicleCoverage, 'deductibleTerms[0].value')
        ) {
          this.coverages +=
            _get(vehicleCoverage, 'name') +
            ': ' +
            '$' +
            _get(vehicleCoverage, 'deductibleTerms[0].value');
        }
        if (this.rentalCoverageValue && _get(vehicleCoverage, 'code') === 'PARentalCov') {
          const dailyLimit = _get(vehicleCoverage, 'limitTerms', []).find(
            limit => limit.description === 'Daily Limit'
          );
          const maxLimit = _get(vehicleCoverage, 'limitTerms', []).find(
            limit => limit.description === 'Maximum'
          );
          this.coverages +=
            _get(vehicleCoverage, 'name') +
            ': ' +
            `$${dailyLimit.value}/Daily Limit, $${maxLimit.value}/Maximum`;
        }
        if (this.ersValue && _get(vehicleCoverage, 'code') === 'PAEmergencyRoadServiceCov_af') {
          this.coverages += _get(vehicleCoverage, 'name');
        }
      }

      const messageModelRequest: CoverageChangeCustomerTemplateDataModel = {
        firstName: this.customerFirstName,
        policyNumber: this.selectedPolicy.policyNumber,
        Vehicle: this.matchingvehicle.description,
        CurrentCoverage: this.coverages || '',
        CoverageChanges: withoutBreak
          ? this.newCoverages.replace(/<br><\/br>/g, ', ')
          : this.newCoverages,
        orderNumber: _get(this.premiumDetails, 'orderNumber', ''),
        producer: this.selectedProducer,
        ProdID: _get(this, 'selectedProducer.id', '')
      };
      return messageModelRequest;
    }
  }

  sendToAgent() {
    this.spinner.start({
      secondaryMessage: `We’re submitting your quote, this may take a minute or two.`,
      blockActions: true
    });
    const customerNotificationRequest: CoverageChangeNotificationRequestCustomerEmail = {
      email: new ReplaceVehicleRequestCustomerEmail(this.getCustomerEmailRequestModel()),
      message: this.getMessageModel(),
      templateId: 'CovChangeToAgent'
    };

    if (_get(customerNotificationRequest, 'message.policyNumber')) {
      customerNotificationRequest.message.policyNumber = this.utilService.maskPolicyNumber(
        customerNotificationRequest.message.policyNumber
      );
    }

    const agentNotificationRequest: CoverageChangeCustomerTemplateDataModel =
      this.getMessageModel(true);

    const notificationMsg = this.copyService.getCopy('policy.ansEmailForCoverageChange', {
      customerName: agentNotificationRequest.firstName,
      policyNumber: agentNotificationRequest.policyNumber,
      vehicleInfo: agentNotificationRequest.Vehicle,
      coverageChanges: agentNotificationRequest.CoverageChanges.replace(/<br><\/br>/g, ', '),
      orderNumber: agentNotificationRequest.orderNumber
    });

    const ansRequest: ANSRequestModel = new ANSRequestModel({
      producer: this.selectedProducer,
      orderNumber: _get(this.premiumDetails, 'orderNumber'),
      policyNumber: _get(this.selectedPolicy, 'policyNumber'),
      from: this.copyService.getCopy('shared.myAccountTxt'),
      customerName: this.customerName,
      notificationText: notificationMsg,
      vehicleId: this.vehicleId,
      notificationType: 'ANSMYBNCOV'
    });

    this.store.dispatch(new CoverageChangeRequestANSNotificationAction(ansRequest));
    this.store.dispatch(new CoverageChangeRequestNotificationAction(agentNotificationRequest));
    this.store.dispatch(
      new CoverageChangeRequestCustomerEmailNotificationAction(customerNotificationRequest)
    );
    this.store.dispatch(new CoverageChangeSetRequestType('SendToAgent'));
  }

  bindQuote() {
    this.spinner.start({
      secondaryMessage: `We’re submitting your quote, this may take a minute or two.`,
      blockActions: true
    });
    const customerNotificationRequest: CoverageChangeNotificationRequestCustomerEmail = {
      email: new ReplaceVehicleRequestCustomerEmail(this.getCustomerEmailRequestModel()),
      message: this.getMessageModel(),
      templateId: 'CovChangeBindConf'
    };

    const customerName = this.customerName;
    const policyNumber = _get(this.selectedPolicy, 'policyNumber', '');
    const vehicle = customerNotificationRequest.message.Vehicle;
    const orderNumber = _get(this.premiumDetails, 'orderNumber');

    const notificationMsg = this.copyService.getCopy('policy.ansEmailForCoverageChangeOnlineBind', {
      customerName: customerName,
      policyNumber: policyNumber,
      vehicleInfo: vehicle,
      coverageChanges: this.getMessageModel(true).CoverageChanges,
      orderNumber: customerNotificationRequest.message.orderNumber
    });

    const internalAgentEmail = _get(this.selectedProducer, 'agencyEmails[0]')
      ? env.policyGracePeriodNotificatonInternalAgencyMailbox
      : env.policyGracePeriodNotificatonInternal;

    const onlineBindReqPayload: SubmitOnlineBindRequestModel = {
      policyNumber: _get(this.selectedPolicy, 'policyNumber', ''),
      state: this.stateCode,
      notificationSubject: 'Your Customer Has Successfully Changed Their Auto Policy',
      notificationText: notificationMsg,
      notificationType: 'ANSMYBNCOV',
      emailSubject: 'Your Customer Has Successfully Changed Their Auto Policy',
      emailBody: _escape(
        this.replaceVehicleService.getAgentEmailContentForCoverageChange(
          this.getMessageModel(true),
          'policy.agentEmailForCoverageChangeOnlineBind',
          _get(this.selectedProducer, 'firstName')
        )
      ),
      workOrderNumber: orderNumber,
      vehicleId: this.vehicleId,
      sourceSystem: 'MyAccountWebApplication',
      customer: {
        firstName: this.customerFirstName,
        lastName: this.customerLastName,
        phone: this.customerPhone,
        email: this.customerEmail,
        emailTemplateId: customerNotificationRequest.templateId
      },
      producer: {
        firstName: this.selectedProducer.firstName,
        lastName: this.selectedProducer.lastName,
        producerId: this.selectedProducer.id,
        phone: _get(this.selectedProducer, 'phones[0].phoneNumber', 'No phone number on record'),
        fromAddress: env.policyChangeRequestInternal,
        fromAddressLabel: this.copyService.getCopy('policy.policyChangeAgentEmailLabel'),
        email: env.production
          ? _get(
              this.selectedProducer,
              'agentEmails[0]',
              _get(this.selectedProducer, 'emails[0]', 'No email address on record')
            )
          : internalAgentEmail
      },
      emailTemplateData: {
        RiskInformation: _get(this.matchingvehicle, 'description', vehicle),
        TotalPremium: _get(this.premiumDetails, 'current.fullCost', 'NA'),
        MonthlyPremium: _get(this.premiumDetails, 'current.monthlyCost', 'NA'),
        TermDescription: _get(this.premiumDetails, 'current.policyTermMonths', 'NA'),
        NewRiskInformation: _get(this, 'newVehicle', 'NA'),
        NewTotalPremium: _get(this.premiumDetails, 'new.fullCost', 'NA'),
        NewMonthlyPremium: _get(this.premiumDetails, 'new.monthlyCost', 'NA'),
        NewTermDescription: _get(this.premiumDetails, 'new.policyTermMonths', 'NA'),
        Drivers: _get(this, 'drivers', 'NA'),
        GaragedAddress: _get(this.matchingvehicle, 'address.addressLine1', 'NA'),
        Coverages: _escape(_get(this, 'newCoverages', 'NA')),
        Fees: _get(this, 'fees', 'NA'),
        Discounts: _get(this, 'discounts', 'NA'),
        FirstName: this.customerFirstName,
        PolicyNumber: this.utilService.maskPolicyNumber(this.selectedPolicy.policyNumber),
        Vehicle: vehicle,
        CurrentCoverage: _escape(this.coverages),
        CoverageChanges: _escape(this.newCoverages),
        SubscriberKey: this.customerEmail,
        EmailAddress: this.customerEmail
      }
    };

    this.store.dispatch(new CoverageChangeOnlineBindRequestAction(onlineBindReqPayload));
    this.store.dispatch(new CoverageChangeSetRequestType('OnlineBind'));
  }

  editCoverages() {
    this.store.dispatch(
      fromRouterActions.Go({
        path: ['/policies/coverage-change/' + this.matchingvehicle.id + '/get-quote']
      })
    );
  }

  private getCustomerEmailRequestModel(): CustomerEmailRequest {
    const customerEmailRequest: CustomerEmailRequest = {
      customerEmailId: this.customerEmail,
      customerId: this.customerId,
      partnerId: this.partnerId
    };
    return customerEmailRequest;
  }
}
